import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ConceptPopulated, DriveCycleRequirementIds, UnitType } from 'src/api';
import { ConceptInputComponent } from '../../../forms/input/concept-input.component';
import {
  ColorBackground,
  ComponentWidth,
  FlexLayout,
} from '@ansys-internal/awc-angular/core';
import { AWCListItem } from '@ansys-internal/awc-angular/list';
import { ConfigurationsService } from 'src/app/shared/services/configurations.service';
import {
  InputSize,
  InputType,
  LabelPosition,
  SwitchComponent,
  SwitchSize,
} from '@ansys-internal/awc-angular/forms';
import { IconComponent, Icons } from '@ansys-internal/awc-angular/icons';
import { State } from '@ansys-internal/awf-angular/store';
import { Subject, takeUntil } from 'rxjs';
import {
  DialogService,
} from '@ansys-internal/awf-angular/shared';
import { ConceptService } from '../../../../../shared/services/concept.service';
import { ActiveConceptState } from '../../../../../state/lib/active-concept.state';
import { ImportDriveCycleComponent } from '../../../../dialogs/import-drive-cycle/import-drive-cycle.component';
import { EventBus } from '../../../../../shared/tools/event-bus';
import {
  DataDisplayService,
  DataDisplayState,
} from '../../../../../shared/services/data-display.service';
import { getSelectedValue } from '../../../../../shared/tools/common.utils';
import { FeatureFlagsEnum } from '../../../../../shared/enums/feature-flags.enum';
import { FeatureFlagModule } from '../../../../../shared/modules/feature-flag.module';
import { TooltipsModule } from '@ansys-internal/awc-angular/tooltips';
import { ModalSize } from '@ansys-internal/awc-angular/modals';
import {FormSelectComponent} from "@ansys-internal/awc-angular/form-select";
import {AWCFormElementSize} from "@ansys-internal/awc-angular/form";

type SetPart = [value: string, type: string, id: string, index?: number];

@Component({
    selector: 'app-drive-cycles-inputs',
    templateUrl: './drive-cycles.component.html',
    styleUrls: ['../inputs.scss'],
    imports: [
        IconComponent,
        SwitchComponent,
        ConceptInputComponent,
        FeatureFlagModule,
        TooltipsModule,
        FormSelectComponent,
    ]
})
export class DriveCyclesComponent implements OnInit {
  @Input() part!: DriveCycleRequirementIds;
  @Input() update!: boolean;
  @Output() setPart: EventEmitter<SetPart> = new EventEmitter<SetPart>();
  @Output() forceUpdate: EventEmitter<string> = new EventEmitter<string>();
  @Output() partsLoaded: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('fileUpload') upload!: ElementRef;
  protected UnitType = UnitType;
  protected InputTypes = InputType;
  protected layout: FlexLayout = FlexLayout.COLUMN;
  protected aerodynamics: AWCListItem[] = [];
  protected masses: AWCListItem[] = [];
  protected wheels: AWCListItem[] = [];
  protected driveCyclesMappedForSelect: AWCListItem[] = [];
  protected limits: AWCListItem[] = [];
  protected loads: AWCListItem[] = [];
  protected readonly ColorBackground = ColorBackground;
  protected readonly InputType = InputType;
  protected selectedAero: (string | null)[] = [];
  protected selectedMass: (string | null)[] = [];
  protected selectedWheel: (string | null)[] = [];
  protected selectedLimit: (string | null)[] = [];
  protected selectedLoad: (string | null)[] = [];
  protected selectedDC!: string;
  protected selectedDCName!: string;
  protected labelPosition: LabelPosition = LabelPosition.BEFORE;
  protected size: SwitchSize = SwitchSize.MEDIUM;
  protected awcSizing: ComponentWidth = ComponentWidth.FILL;
  protected iconButton: Icons = Icons.ADD_GEOMETRY;
  protected partsLoadedEmitted: boolean = false;
  protected readonly InputSize = InputSize;
  protected readonly Icons = Icons;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private previousDriveCycleId: string | null = null;
  private bus: EventBus = EventBus.getInstance();

  constructor(
    private configurationService: ConfigurationsService,
    private conceptService: ConceptService,
    private state: State,
    private dialogService: DialogService,
    private displayService: DataDisplayService
  ) {
    this.state
      .get(ActiveConceptState)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (concept?: ConceptPopulated) => {
        if (!concept) return;

        this.getDriveCycleData(concept);
      });

    this.bus.register('drive-cycle:select:update', async () => {
      this.selectedDCName = this.getDriveCycleName();

      if (!this.selectedDCName) {
        // If we have a newly uploaded file, we'll have to re-fetch the drive cycle data
        const concept = this.state.value(ActiveConceptState);
        if (concept) {
          await this.getDriveCycleData(concept);
        }

        this.selectedDCName = this.getDriveCycleName();
      }

      this.selectedDC = this.part.drive_cycle_id;
      this.previousDriveCycleId = this.part.drive_cycle_id;
      this.displayService.displayState = DataDisplayState.GRAPH_DISPLAY;
    });
  }

  async getDriveCycleData(concept: ConceptPopulated): Promise<void> {
    const driveCycles: any = await this.conceptService.getConceptDriveCycles(
      concept
    );

    this.driveCyclesMappedForSelect = driveCycles.map((driveCycle: any) => {
      return { id: driveCycle.id, text: driveCycle.name } as AWCListItem;
    });

    this.partsLoadedEmitted = true;
    this.partsLoaded.emit(this.partsLoadedEmitted);
    if (this.part?.drive_cycle_id) {
      this.selectedDCName = this.driveCyclesMappedForSelect.find(
        (driveCycle) => {
          return driveCycle.id === this.part.drive_cycle_id;
        }
      )?.text as string;
    }
    return Promise.resolve();
  }

  ngOnInit(): void {
    this.partsLoaded.emit(this.partsLoadedEmitted);

    [this.aerodynamics, this.masses, this.wheels, this.limits, this.loads] =
      this.configurationService.configurationLists.map((list) => {
        return list.map((item) => {
          return { ...item };
        });
      });

    this.selectedAero = [
      getSelectedValue(this.update, this.part.aero_id, this.aerodynamics),
    ];
    this.part.aero_id = this.selectedAero[0] || '';

    this.selectedMass = [
      getSelectedValue(this.update, this.part.mass_id, this.masses),
    ];
    this.part.mass_id = this.selectedMass[0] || '';

    this.selectedWheel = [
      getSelectedValue(this.update, this.part.wheel_id, this.wheels),
    ];
    this.part.wheel_id = this.selectedWheel[0] || '';

    this.selectedLimit = [
      getSelectedValue(
        this.update,
        this.part.deceleration_limit_id,
        this.limits
      ),
    ];
    this.part.deceleration_limit_id = this.selectedLimit[0];

    this.selectedLoad = [
      getSelectedValue(this.update, this.part.ancillary_load_id, this.loads),
    ];
    this.part.ancillary_load_id = this.selectedLoad[0];

    this.selectedDC = this.part.drive_cycle_id;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  protected selectionIDSet($event: string | null, type: string): void {
    if ($event === 'null') {
      $event = null;
    }

    this.part[type as keyof DriveCycleRequirementIds] = $event as never;
    this.forceUpdate.emit('');
  }

  protected addDriveCycle(): void {
    this.dialogService.open(ImportDriveCycleComponent, {
      size: ModalSize.FULL_SCREEN,
      data: {
        selectedDriveCycle: this.selectedDC,
        part: this.part,
        onUpdate: () => {
          this.forceUpdate.emit('');
        },
      },
    });
  }

  protected toggleThermalAnalysis($event: boolean): void {
    this.part.thermal_analysis = $event;
    this.forceUpdate.emit('');
  }

  protected toggleStopAtThermalLimit($event: boolean): void {
    this.part.stop_at_temperature_limit = $event;
    this.forceUpdate.emit('');
  }

  protected toggleIFullRange($event: boolean): void {
    this.part.full_range_calculation = $event;
    this.forceUpdate.emit('');
  }

  private getDriveCycleName(): string {
    return this.driveCyclesMappedForSelect.find((driveCycle) => {
      return driveCycle.id === this.part.drive_cycle_id;
    })?.text as string;
  }

  protected readonly FeatureFlagsEnum = FeatureFlagsEnum;
  protected readonly AWCFormElementSize = AWCFormElementSize;
}
